import { useMemo } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { useEvent } from "shared/lib/useEvent"
import { Room } from "shared/types/Room"

import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useSharedRoom } from "features/room-management/model/useSharedRoom"
import { useRoomForm } from "widgets/room/room-form/model/useRoomForm"

import { ProjectPageRoomSidebarModel } from "../ProjectPageRoomSidebar"

export interface UseSharedProjectPageRoomSidebarModelArgs {
  projectID: string
  room: Room
}

export function useSharedProjectPageRoomSidebarModel({
  projectID,
  room
}: UseSharedProjectPageRoomSidebarModelArgs): ProjectPageRoomSidebarModel {
  const {
    pendingElementIDs,
    sidebarIsEditing,
    sidebarActiveTab,
    enterSidebarEditing,
    exitSidebarEditing,
    setSidebarActiveTab
  } = useProjectStore(projectID)
  const isPending = pendingElementIDs.includes(room.id)

  const { price: isPriceServiceAvailable } = useAPIHealth()

  const roomModel = useSharedRoom({
    roomID: room.id,
    projectID,
    options: { includePrice: true }
  })

  const handleSubmit = useEvent(() => undefined)

  const { parent, displayPosition } = useProjectChildPosition({
    child: room,
    projectID
  })

  const roomFormModel = useRoomForm({
    room,
    projectID,
    parentID: parent?.id,
    displayPosition,
    clarificationRequest: roomModel.clarificationRequest,
    onSubmit: handleSubmit
  })

  const productTotal = useMemo(
    () => getProductTotal(room.children),
    [room.children]
  )

  const { data: priceData, isLoading: isLoadingPrice = false } =
    roomModel.pricing.data.price ?? {}

  return {
    projectID,
    room,
    price: isPriceServiceAvailable ? priceData : null,
    isLoadingPrice: isPriceServiceAvailable ? isLoadingPrice : false,
    isPriceServiceAvailable,
    productTotal,
    isPending,
    isDisabled: false,
    roomFormModel,
    callbacks: {
      onEdit: undefined,
      onDelete: undefined,
      onDuplicate: undefined,
      onCopy: undefined,
      onAddClarificationRequest: undefined
    },
    hideControls: true,
    isEditing: sidebarIsEditing,
    enterEditing: enterSidebarEditing,
    exitEditing: exitSidebarEditing,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
